import { supabase } from './supabase';

export type ProductionStatus = 'not_started' | 'in_progress' | 'done';
export type StoreType = 'branding' | 'premium' | 'start' | 'pontual';
export type GoalType = 'delivered_stores' | 'new_stores' | 'office';

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  active: boolean;
  created_at: string;
  deleted_at?: string;
}

export interface Store {
  id: string;
  name: string;
  plan: StoreType;
  status: {
    idVisual: ProductionStatus;
    banners: ProductionStatus;
    sm: ProductionStatus;
    motion: ProductionStatus;
  };
  deadline: string;
  created_at: string;
  completed_at: string | null;
  team_members: TeamMember[];
  price: number;
  costs: {
    sm: number;
    banners: number;
    motion: number;
  };
}

export interface Goal {
  id: string;
  goal_type: GoalType;
  target: number;
  current: number;
  description?: string;
  month: number;
  year: number;
  created_at: string;
  updated_at: string;
}

export const getDefaultPrice = (plan: StoreType): number => {
  switch (plan) {
    case 'branding':
      return 897;
    case 'premium':
      return 397;
    case 'start':
      return 250;
    default:
      return 0;
  }
};

export const getDefaultCosts = (plan: StoreType): { sm: number; banners: number; motion: number } => {
  switch (plan) {
    case 'branding':
      return {
        sm: 80,
        banners: 250,
        motion: 180
      };
    case 'premium':
      return {
        sm: 200,
        banners: 0,
        motion: 0
      };
    case 'start':
      return {
        sm: 120,
        banners: 0,
        motion: 0
      };
    default:
      return {
        sm: 0,
        banners: 0,
        motion: 0
      };
  }
};

export const visionService = {
  // Team Members
  async getTeamMembers(includeInactive = false) {
    let query = supabase
      .from('team_members')
      .select('*')
      .order('name');

    if (!includeInactive) {
      query = query.eq('active', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as TeamMember[];
  },

  async addTeamMember(member: Omit<TeamMember, 'id' | 'created_at' | 'active' | 'deleted_at'>) {
    const { data, error } = await supabase
      .from('team_members')
      .insert([{ ...member, active: true }])
      .select()
      .single();

    if (error) throw error;
    return data as TeamMember;
  },

  async updateTeamMember(id: string, updates: Partial<TeamMember>) {
    const { data, error } = await supabase
      .from('team_members')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as TeamMember;
  },

  async deactivateTeamMember(id: string) {
    const { data, error } = await supabase
      .from('team_members')
      .update({
        active: false,
        deleted_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as TeamMember;
  },

  // Stores
  async getStores() {
    const { data, error } = await supabase
      .from('stores')
      .select(`
        *,
        team_members:store_team_members(
          member:team_members(*)
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data?.map(store => ({
      ...store,
      team_members: store.team_members?.map((tm: any) => tm.member) || [],
      price: store.price || getDefaultPrice(store.plan),
      costs: store.costs || getDefaultCosts(store.plan)
    })) as Store[]) || [];
  },

  async getStoresByMonth(month: number, year: number) {
    const startDate = new Date(year, month - 1, 1).toISOString();
    const endDate = new Date(year, month, 0).toISOString();

    const { data, error } = await supabase
      .from('stores')
      .select(`
        *,
        team_members:store_team_members(
          member:team_members(*)
        )
      `)
      .gte('created_at', startDate)
      .lte('created_at', endDate)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data?.map(store => ({
      ...store,
      team_members: store.team_members?.map((tm: any) => tm.member) || [],
      price: store.price || getDefaultPrice(store.plan),
      costs: store.costs || getDefaultCosts(store.plan)
    })) as Store[]) || [];
  },

  async addStore(store: Omit<Store, 'id' | 'created_at' | 'deadline' | 'completed_at' | 'team_members' | 'price' | 'costs'>, memberIds: string[]) {
    const { data: newStore, error: storeError } = await supabase
      .from('stores')
      .insert([{
        name: store.name,
        plan: store.plan,
        status: {
          idVisual: 'not_started',
          banners: 'not_started',
          sm: 'not_started',
          motion: 'not_started'
        },
        price: getDefaultPrice(store.plan),
        costs: getDefaultCosts(store.plan),
        deadline: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString()
      }])
      .select()
      .single();

    if (storeError) throw storeError;

    // Adicionar membros à loja
    if (memberIds.length > 0) {
      const { error: membersError } = await supabase
        .from('store_team_members')
        .insert(
          memberIds.map(memberId => ({
            store_id: newStore.id,
            member_id: memberId
          }))
        );

      if (membersError) throw membersError;
    }

    return newStore;
  },

  async updateStore(id: string, store: Partial<Store>, memberIds: string[]) {
    // Atualizar informações da loja
    const { data: currentStore } = await supabase
      .from('stores')
      .select('plan, status')
      .eq('id', id)
      .single();

    const newPlan = store.plan || currentStore.plan;
    const defaultPrice = getDefaultPrice(newPlan);
    const defaultCosts = getDefaultCosts(newPlan);

    // Verifica se todos os status são 'done'
    const newStatus = store.status || currentStore.status;
    const allDone = Object.values(newStatus).every(status => status === 'done');

    const { error: storeError } = await supabase
      .from('stores')
      .update({
        ...store,
        plan: newPlan,
        price: store.price !== undefined ? store.price : defaultPrice,
        costs: store.costs || defaultCosts,
        completed_at: allDone ? new Date().toISOString() : null
      })
      .eq('id', id);

    if (storeError) throw storeError;

    // Remover membros existentes
    const { error: deleteError } = await supabase
      .from('store_team_members')
      .delete()
      .eq('store_id', id);

    if (deleteError) throw deleteError;

    // Adicionar novos membros
    if (memberIds.length > 0) {
      const { error: membersError } = await supabase
        .from('store_team_members')
        .insert(
          memberIds.map(memberId => ({
            store_id: id,
            member_id: memberId
          }))
        );

      if (membersError) throw membersError;
    }
  },

  async getStoreById(id: string) {
    const { data, error } = await supabase
      .from('stores')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Store;
  },

  async deleteStore(id: string) {
    const { error } = await supabase
      .from('stores')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getMetrics(viewMode: 'all' | 'monthly' = 'monthly', month?: number, year?: number) {
    const { data: stores, error } = await supabase
      .from('stores')
      .select('*');

    if (error) throw error;

    let filteredStores = stores;
    if (viewMode === 'monthly' && month && year) {
      const startDate = new Date(year, month - 1, 1).getTime();
      const endDate = new Date(year, month, 0).getTime();
      filteredStores = stores.filter(store => {
        const storeDate = new Date(store.created_at).getTime();
        return storeDate >= startDate && storeDate <= endDate;
      });
    }

    const now = new Date().getTime();
    
    return {
      totalStores: filteredStores.length,
      inProgressStores: filteredStores.filter(store => !store.completed_at).length,
      completedStores: filteredStores.filter(store => store.completed_at).length,
      overdueTasks: filteredStores.filter(store => {
        if (store.completed_at) return false;
        const deadline = new Date(store.deadline).getTime();
        return deadline < now;
      }).length,
      // Adicionar mais métricas conforme necessário
    };
  },

  async getFinanceMetrics() {
    const { data: stores } = await supabase
      .from('stores')
      .select('*');

    if (!stores) return null;

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    // Get goals for current month
    const { data: goals } = await supabase
      .from('vision_goals')
      .select('*')
      .eq('month', currentMonth)
      .eq('year', currentYear);

    // Calculate metrics
    const revenue = stores.reduce((sum, store) => sum + (store.price || 0), 0);
    const profit = stores.reduce((sum, store) => {
      const costs = store.costs || { sm: 0, banners: 0, motion: 0 };
      const totalCost = costs.sm + costs.banners + costs.motion;
      return sum + ((store.price || 0) - totalCost);
    }, 0);

    // Update goals progress
    if (goals) {
      for (const goal of goals) {
        let current = 0;
        switch (goal.goal_type) {
          case 'delivered_stores':
            current = stores.filter(store => store.completed_at).length;
            break;
          case 'new_stores':
            current = stores.filter(store => {
              const createdAt = new Date(store.created_at);
              return createdAt >= new Date(currentDate.setMonth(currentDate.getMonth() - 1)).getTime() / 1000;
            }).length;
            break;
          case 'office':
            current = stores.filter(store => store.plan === 'office').length;
            break;
        }
        await this.updateGoalProgress(goal.id, current);
      }
    }

    return {
      revenue,
      profit,
      goals: goals?.filter(g => g.goal_type === 'delivered_stores' || g.goal_type === 'new_stores' || g.goal_type === 'office') || []
    };
  },

  async getGoals(viewMode: 'all' | 'monthly' = 'monthly', month?: number, year?: number) {
    let query = supabase.from('vision_goals').select('*');
    
    if (viewMode === 'monthly' && month && year) {
      query = query
        .eq('month', month)
        .eq('year', year);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as Goal[];
  },

  async upsertGoal(goal: Omit<Goal, 'id' | 'current' | 'created_at' | 'updated_at'>) {
    if (!goal.goal_type || !goal.target || !goal.month || !goal.year) {
      throw new Error('Campos obrigatórios faltando');
    }

    try {
      // Verificar se já existe uma meta do mesmo tipo para o mesmo mês/ano
      const { data: existingGoal, error: findError } = await supabase
        .from('vision_goals')
        .select('id')
        .eq('goal_type', goal.goal_type)
        .eq('month', goal.month)
        .eq('year', goal.year)
        .single();

      if (findError && findError.code !== 'PGRST116') {
        throw findError;
      }

      if (existingGoal?.id) {
        // Atualizar meta existente
        const { data, error } = await supabase
          .from('vision_goals')
          .update({
            target: goal.target,
            description: goal.description,
            current: 0
          })
          .eq('id', existingGoal.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Criar nova meta
        const { data, error } = await supabase
          .from('vision_goals')
          .insert({
            goal_type: goal.goal_type,
            target: goal.target,
            description: goal.description,
            month: goal.month,
            year: goal.year,
            current: 0
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Erro ao criar/atualizar meta:', error);
      throw error;
    }
  },

  async updateGoal(id: string, updates: { target?: number; description?: string }) {
    if (!id) {
      throw new Error('ID da meta é obrigatório');
    }

    if (updates.target !== undefined && updates.target <= 0) {
      throw new Error('Meta deve ser maior que zero');
    }

    try {
      const { data, error } = await supabase
        .from('vision_goals')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao atualizar meta:', error);
      throw error;
    }
  },

  async updateGoalProgress(id: string, current: number) {
    if (!id) {
      throw new Error('ID da meta é obrigatório');
    }

    if (typeof current !== 'number' || current < 0) {
      throw new Error('Valor atual inválido');
    }

    try {
      const { data, error } = await supabase
        .from('vision_goals')
        .update({ current })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao atualizar progresso:', error);
      throw error;
    }
  },

  async deleteGoal(id: string) {
    const { error } = await supabase
      .from('vision_goals')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};
